import Recaptcha from '@components/App/RecaptchaWidget'
import { Tabs } from '@lib/components'
import type React from 'react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { PageNameSource } from '@/types'
import type { ModalProps } from '@/types/modal'
import { CrawlerTab, FileTab, SitemapTab } from './components/Tabs'
import { SELECTORS } from './components/Tabs/constants'
import { prepareUrl } from './components/Tabs/utils'
import css from './Import.module.scss'

type Props = {
  teamId?: string
  folderId?: string | null
  defaultUrl: string | null
  immediately: boolean
  initialBlocks?: boolean
  initialBlocksContent?: boolean
  initialExcludedUrls?: string[]
  initialPageNames?: PageNameSource
  // Land the crawl in the project the user already has open. Hides the File (.xml) tab,
  // which carries a whole project of its own — sections, tags, colours — and has no
  // agreed merge behaviour for a project that already exists.
  intoOpenProject?: boolean
}

// Tabs are addressed by id, not position: the set of tabs differs between the dashboard
// and the editor, and the `.xml` auto-switch below used to hard-code index 2.
type TabId = 'crawler' | 'file' | 'sitemap'

const detectSitemapXml = (url: string): boolean =>
  prepareUrl(url).endsWith('.xml')

const Import: React.FC<ModalProps & Props> = ({
  teamId,
  folderId,
  closeButton,
  defaultUrl,
  immediately,
  intoOpenProject = false,
  initialBlocks = false,
  initialBlocksContent = false,
  initialExcludedUrls = [],
  initialPageNames = 'url',
}) => {
  const [url, setUrl] = useState<string>(defaultUrl || '')
  const [activeTabId, setActiveTabId] = useState<TabId>(
    detectSitemapXml(url) ? 'sitemap' : 'crawler'
  )
  const [activeSelector, setActiveSelector] = useState<string>(
    initialBlocks ? SELECTORS.WITH_BLOCKS : SELECTORS.WITH_META_TAGS
  )

  const immediatelyRef = useRef(immediately)

  const setActiveTabAndUrl = (tabId: TabId, nextUrl: string) => {
    setUrl(nextUrl)
    setActiveTabId(tabId)
  }

  useLayoutEffect(() => {
    if (detectSitemapXml(url)) {
      setActiveTabAndUrl('sitemap', prepareUrl(url))
    }
  }, [url])

  // The crawler tab has its own two cards (standard, AI blocks) and keeps the
  // meta-tag choice in its options, so either of them means "With meta tags"
  // on the sitemap tab; only "Pages only" picked there survives the round trip.
  const sitemapSelector =
    activeSelector === SELECTORS.PAGES_ONLY
      ? SELECTORS.PAGES_ONLY
      : SELECTORS.WITH_META_TAGS

  const tabs = useMemo(() => {
    const all = [
      {
        id: 'crawler' as TabId,
        tab: { title: 'Crawler' },
        Component: CrawlerTab,
        componentProps: {
          intoOpenProject,
          teamId,
          folderId,
          url,
          setUrl,
          activeSelector,
          immediatelyRef,
          setActiveSelector,
          initialBlocksContent,
          initialExcludedUrls,
          initialPageNames,
        },
      },
      {
        id: 'file' as TabId,
        tab: { title: 'File' },
        Component: FileTab,
        componentProps: {
          teamId,
        },
      },
      {
        id: 'sitemap' as TabId,
        tab: { title: 'Sitemap.xml' },
        Component: SitemapTab,
        componentProps: {
          intoOpenProject,
          teamId,
          folderId,
          url,
          immediatelyRef,
          activeSelector: sitemapSelector,
          setActiveSelector,
          setUrl,
          setActiveTabAndUrl,
        },
      },
    ]

    return intoOpenProject ? all.filter(({ id }) => id !== 'file') : all
  }, [
    teamId,
    folderId,
    intoOpenProject,
    url,
    activeSelector,
    initialBlocksContent,
    initialExcludedUrls,
    initialPageNames,
  ])

  const selectedIndex = Math.max(
    tabs.findIndex(({ id }) => id === activeTabId),
    0
  )

  const onSelectTab = (index: number) => {
    const nextId = tabs[index]?.id ?? 'crawler'

    if (nextId === 'crawler' && detectSitemapXml(url)) {
      setUrl(prepareUrl(url).slice(0, -4))
    }

    setActiveTabId(nextId)
  }

  return (
    <Recaptcha>
      <div className={css.container}>
        <div className={css.heading}>
          <div className={css.title}>Import</div>

          <div className={css.close}>{closeButton}</div>
        </div>

        <Tabs
          tabs={tabs}
          selectedIndex={selectedIndex}
          onSelect={onSelectTab}
        />
      </div>
    </Recaptcha>
  )
}

export default Import
